import { createLogger } from '@svenskaspelab/nova-core/log';
import { type QueryClient, type QueryFunction, type QueryKey } from '@tanstack/react-query';

import { getQueryClient } from './getQueryClient.js';

const logger = createLogger('tanstack-prefetch');

type PrefetchQueryOptions<TData, TQueryKey extends QueryKey> = {
  queryFn: QueryFunction<TData, TQueryKey>;
  queryKey: TQueryKey;
};

// Rule turned off because faulty error:
// "This rule was recently added, and has a surprising amount of hidden complexity compared to most of our rules."
// see: https://typescript-eslint.io/rules/no-unnecessary-type-parameters/

/**
 * A prefetchQuery function to preload (fetch) data on the server.
 *
 * @param options Fetch options containing fetcher etc.
 * @param queryClient Client to seed. Pass the client that is dehydrated, otherwise the data is lost.
 * @returns Prefetch promise
 */
export function prefetchQuery<TData, TQueryKey extends QueryKey>(
  options: PrefetchQueryOptions<TData, TQueryKey>,
  queryClient: QueryClient = getQueryClient()
): Promise<void> {
  // query() rejects on failure, so swallow to keep prefetch fire-and-forget semantics.
  return queryClient.query<TData, Error, TData, TData, TQueryKey>({ ...options }).then(
    () => {},
    (error: unknown) => {
      logger.warn('Prefetch failed', { error, queryKey: options.queryKey });
    }
  );
}
